/*
 * HC design system accordion controller.
 *
 * Ported from design/round-3/js/app.js. Markup stays exactly as
 * snippets/hc-accordion.liquid writes it:
 *
 *   <div class="accordion" data-hc-accordion-group="faq">
 *     <details class="accordion__item">
 *       <summary class="accordion__summary">...</summary>
 *       <div class="accordion__panel">...</div>
 *     </details>
 *   </div>
 *
 * On first sight the controller wraps the panel in <div class="accordion__wrap">.
 * That wrapper is the only animated element: grid-template-rows goes from
 * minmax(0, 0fr) to minmax(0, 1fr) over 300ms on one easing. Nothing is
 * measured, so there is no forced reflow and no jump at either end. The
 * <details> keeps `open` for the whole close and loses it on transitionend,
 * and reversing mid-flight is the same transition running the other way.
 *
 * Events, both usable by section scripts:
 *   - native `toggle` on the <details>. Fires on open at the start and on
 *     close at the end. It does not bubble, so listen in the capture phase.
 *   - `hc:accordion` bubbles from the row once the animation has finished,
 *     detail { item, open, group }. Prefer this for anything that measures
 *     layout.
 *
 * One row per group stays open. Put data-accordion-multi on the .accordion
 * container for groups that may have several rows open at once.
 *
 * prefers-reduced-motion: reduce skips the transition and settles at once.
 */
(function () {
  'use strict';

  /* Keep in sync with --hc-t-accordion in assets/hc-tokens.css. */
  var ACCORDION_MS = 300;

  function all(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function group(item) {
    return item.closest('.accordion');
  }

  function groupName(item) {
    var container = group(item);
    if (!container) return null;
    return container.getAttribute('data-hc-accordion-group') || item.getAttribute('data-hc-accordion-item') || null;
  }

  /** Direct rows of a group, skipping rows that belong to a nested accordion. */
  function rows(container) {
    return all('.accordion__item', container).filter(function (item) {
      return group(item) === container;
    });
  }

  function childWithClass(el, cls) {
    for (var i = 0; i < el.children.length; i++) {
      var kid = el.children[i];
      if (kid.classList && kid.classList.contains(cls)) return kid;
    }
    return null;
  }

  /**
   * The animated wrapper, created around .accordion__panel the first time a
   * row is seen and wired for transitionend once. Null for a row with no panel.
   */
  function wrapOf(item) {
    var wrap = childWithClass(item, 'accordion__wrap');
    if (wrap) return wrap;

    var panel = childWithClass(item, 'accordion__panel');
    if (!panel) return null;

    wrap = document.createElement('div');
    wrap.className = 'accordion__wrap';
    item.insertBefore(wrap, panel);
    wrap.appendChild(panel);
    wrap.addEventListener('transitionend', function (event) {
      if (event.target !== wrap || event.propertyName !== 'grid-template-rows') return;
      settle(item);
    });
    return wrap;
  }

  function announce(item, open) {
    item.dispatchEvent(
      new CustomEvent('hc:accordion', {
        bubbles: true,
        detail: { item: item, open: open, group: groupName(item) }
      })
    );
  }

  function clearTimer(item) {
    if (item.hcAccordionTimer) {
      clearTimeout(item.hcAccordionTimer);
      item.hcAccordionTimer = 0;
    }
  }

  /** End of the transition, or of its safety timeout: commit the state. */
  function settle(item) {
    clearTimer(item);
    if (!item.hcAccordionPending) return;
    item.hcAccordionPending = false;
    var open = item.classList.contains('is-expanded');
    if (!open) item.open = false;
    announce(item, open);
  }

  /** Safety net for a browser that will not interpolate fr units. */
  function arm(item) {
    item.hcAccordionPending = true;
    clearTimer(item);
    item.hcAccordionTimer = setTimeout(function () {
      item.hcAccordionTimer = 0;
      settle(item);
    }, ACCORDION_MS + 120);
  }

  function openRow(item) {
    var wrap = wrapOf(item);
    item.hcAccordionWant = 'open';

    if (!wrap || prefersReducedMotion()) {
      clearTimer(item);
      item.hcAccordionPending = false;
      item.open = true;
      item.classList.add('is-expanded');
      announce(item, true);
      return;
    }

    if (item.open) {
      /* Reversing a close: the panel is still laid out, so flipping the class
         now transitions from wherever the row currently sits. */
      item.classList.add('is-expanded');
      arm(item);
      return;
    }

    item.open = true; /* renders the panel at 0fr */
    arm(item);
    requestAnimationFrame(function () {
      if (item.hcAccordionWant !== 'open') return;
      item.classList.add('is-expanded');
    });
  }

  function closeRow(item, animated) {
    var wrap = wrapOf(item);
    item.hcAccordionWant = 'closed';

    if (!item.open) {
      item.classList.remove('is-expanded');
      return;
    }

    /* Nothing to animate away if the row never reached its open height. */
    var instant = !animated || !wrap || prefersReducedMotion() || !item.classList.contains('is-expanded');
    item.classList.remove('is-expanded');

    if (instant) {
      clearTimer(item);
      item.hcAccordionPending = false;
      item.open = false;
      announce(item, false);
      return;
    }

    arm(item);
  }

  function toggleRow(item) {
    if (item.open && item.classList.contains('is-expanded')) {
      closeRow(item, true);
      return;
    }

    var container = group(item);
    if (container && !container.hasAttribute('data-accordion-multi')) {
      rows(container).forEach(function (other) {
        if (other !== item && other.open) closeRow(other, true);
      });
    }

    openRow(item);
  }

  /**
   * Wrap and wire every row in scope and normalise authored state: at most one
   * open row per exclusive group. Safe to call repeatedly.
   */
  function init(scope) {
    all('.accordion', scope || document).forEach(function (container) {
      var exclusive = !container.hasAttribute('data-accordion-multi');
      var seen = false;

      rows(container).forEach(function (item) {
        wrapOf(item);

        if (!item.open) {
          item.classList.remove('is-expanded');
          return;
        }
        if (exclusive && seen) {
          item.open = false;
          item.classList.remove('is-expanded');
          return;
        }
        seen = true;
        item.classList.add('is-expanded');
      });
    });
  }

  document.addEventListener('click', function (event) {
    var summary = event.target.closest && event.target.closest('.accordion__summary');
    if (!summary) return;

    var item = summary.parentElement;
    if (!item || item.tagName !== 'DETAILS') return;
    if (!group(item)) return;

    event.preventDefault();
    toggleRow(item);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme Editor re-renders a section without a page load. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /* Sections that inject accordion markup after load call this themselves. */
  window.HCAccordion = { init: init, toggle: toggleRow, open: openRow, close: closeRow };
})();
